import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Chip,
  FormControlLabel,
  Link,
  Paper,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { pageForFallacy } from "@features/learn/learnLinks";
import type { FallacyName } from "../utils/argumentChecks";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  setEvaluationMode,
  setGradingViewEvaluation,
} from "@store/slices/workspaceSlice";
import {
  selectArgumentEvaluation,
  selectIsGradingViewActive,
  selectViewedEvaluationMode,
  selectHasSettingNode,
} from "@store/selectors";
import { selectIsDemoMode } from "@store/slices/viewSlice";
import { TOUR } from "@features/onboarding/tourTargets";

/**
 * Floating toolbar at the top-right of the canvas. Toggles Parsons
 * evaluation mode and surfaces a summary of condition violations.
 */
const CanvasEvaluationToolbar = () => {
  const dispatch = useAppDispatch();
  const evaluationMode = useAppSelector(selectViewedEvaluationMode);
  const evaluation = useAppSelector(selectArgumentEvaluation);
  const hasSettingNode = useAppSelector(selectHasSettingNode);
  const isGradingView = useAppSelector(selectIsGradingViewActive);
  const isDemoMode = useAppSelector(selectIsDemoMode);

  const violationCount = evaluation?.diagnostics.length ?? 0;
  const hasCycles = evaluation?.violatedConditions.has("4") ?? false;
  const showNoSettingHint = evaluationMode && !hasSettingNode;
  const [issuesAnchor, setIssuesAnchor] = useState<HTMLElement | null>(null);

  // Distinct diagnostics present, in the order the checks raised them —
  // the chip counts nodes, but a reader wants to know what *kinds* of
  // problem they have, and each kind has an explanation behind it.
  // `fallacy` is optional on a diagnostic, so a check can report a
  // problem it has no traditional name for. Those still count toward the
  // chip; they just have no page to point at.
  const flaggedFallacies = useMemo(() => {
    const seen: FallacyName[] = [];
    for (const diagnostic of evaluation?.diagnostics ?? []) {
      const { fallacy } = diagnostic;
      if (fallacy && !seen.includes(fallacy)) seen.push(fallacy);
    }
    return seen;
  }, [evaluation]);

  const handleToggle = (_: unknown, checked: boolean) => {
    // In grading view the toggle is a session-only overlay — the
    // contributor's persisted evaluation_mode is read-only.
    if (isGradingView) {
      dispatch(setGradingViewEvaluation({ enabled: checked }));
    } else {
      dispatch(setEvaluationMode({ enabled: checked }));
    }
  };

  return (
    <Paper
      data-tour={TOUR.evaluationToggle}
      elevation={2}
      sx={{
        // The canvas container is fixed at top:0 and sits behind ArgNavbar
        // (a MUI AppBar ~64px tall). Offset below the navbar so the toolbar
        // is visible. In demo mode the AppBar grows by the DemoBanner's height.
        position: "absolute",
        top: isDemoMode ? 112 : 76,
        right: 32,
        zIndex: 10,
        px: 1.5,
        py: 0.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        border: 1,
        borderColor: "divider",
      }}
    >
      <Tooltip title="Highlights violations of Parsons' argument-success conditions on the directed subgraph of arrows.">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={evaluationMode}
              onChange={handleToggle}
            />
          }
          label={
            <Typography variant="body2">Evaluation Mode</Typography>
          }
        />
      </Tooltip>

      {evaluationMode && violationCount > 0 && (
        <>
          <Tooltip title="What these mean">
            <Chip
              size="small"
              color={hasCycles ? "error" : "warning"}
              label={`${violationCount} ${violationCount === 1 ? "issue" : "issues"}`}
              onClick={(event) => setIssuesAnchor(event.currentTarget)}
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
          <Popover
            open={Boolean(issuesAnchor)}
            anchorEl={issuesAnchor}
            onClose={() => setIssuesAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Box sx={{ p: 2, maxWidth: 320 }}>
              <Typography variant="subtitle2" gutterBottom>
                What the checks found
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {flaggedFallacies.map((fallacy) => {
                  const page = pageForFallacy(fallacy);
                  return (
                    <Box key={fallacy}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {page.label}
                      </Typography>
                      <Link
                        component={RouterLink}
                        to={`/learn/${page.slug}`}
                        variant="body2"
                        color="secondary"
                        underline="hover"
                      >
                        Why this matters
                      </Link>
                    </Box>
                  );
                })}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2 }}
              >
                Hover a flagged node to see what was found there.
              </Typography>
            </Box>
          </Popover>
        </>
      )}

      {evaluationMode && violationCount === 0 && hasSettingNode && (
        <Chip size="small" color="success" variant="outlined" label="No issues" />
      )}

      {showNoSettingHint && (
        <Tooltip title={`Tag at least one node with the setting role to enable Condition [1] checks.`}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Chip size="small" variant="outlined" label="No setting node" />
          </Box>
        </Tooltip>
      )}
    </Paper>
  );
};

export default CanvasEvaluationToolbar;
